import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { queryKeys } from '@/lib/query-keys';
import { apiClient } from '@borradh-workspace/api-client';
import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { useAudioPreview } from '../hooks/use-audio-preview';
interface Track {
  id: string;
  name: string;
  artist?: string;
  source: 'library' | 'upload';
  bpm: number | null;
  durationSec: number | null;
  energy: string;
  startSec: number;
  playUrl: string;
}
export function PremiumMusicDirectory() {
  const [query, setQuery] = useState('');
  const [energy, setEnergy] = useState('');
  const [minBpm, setMinBpm] = useState('');
  const [maxBpm, setMaxBpm] = useState('');
  const [formError, setFormError] = useState<string | null>(null);
  const [search, setSearch] = useState('limit=50');
  const { playingId, toggle, stop } = useAudioPreview();
  const result = useQuery({
    queryKey: queryKeys.videos.premiumMusic(search),
    queryFn: () =>
      apiClient.get<{ tracks: Track[]; uploads: Track[]; total: number }>(
        `videos/premium/music?${search}`
      ),
    staleTime: 60000,
  });
  const tracks = [
    ...(result.data?.uploads ?? []),
    ...(result.data?.tracks ?? []),
  ];
  return (
    <div className="space-y-6">
      <form
        className="grid gap-4 sm:grid-cols-2 lg:grid-cols-5"
        onSubmit={(event) => {
          event.preventDefault();
          if (minBpm && maxBpm && Number(minBpm) > Number(maxBpm)) {
            setFormError('Minimum BPM must not exceed maximum BPM.');
            return;
          }
          setFormError(null);
          stop();
          const params = new URLSearchParams({ query, limit: '50' });
          if (energy) params.set('energy', energy);
          if (minBpm) params.set('minBpm', minBpm);
          if (maxBpm) params.set('maxBpm', maxBpm);
          setSearch(params.toString());
        }}
      >
        <label
          htmlFor="premium-music-query"
          className="space-y-2 lg:col-span-2"
        >
          <span className="text-sm font-medium">Track or artist</span>
          <Input
            id="premium-music-query"
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            maxLength={120}
            placeholder="Search music, e.g. House"
          />
        </label>
        <label className="space-y-2">
          <span className="text-sm font-medium">Tempo energy</span>
          <select
            className="h-10 w-full rounded-md border bg-background px-3"
            value={energy}
            onChange={(event) => setEnergy(event.target.value)}
          >
            <option value="">All tempos</option>
            <option value="relaxed">Relaxed · under 100 BPM</option>
            <option value="steady">Steady · 100–114 BPM</option>
            <option value="driving">Driving · 115+ BPM</option>
          </select>
        </label>
        <label htmlFor="premium-music-min-bpm" className="space-y-2">
          <span className="text-sm font-medium">Minimum BPM</span>
          <Input
            type="number"
            min={40}
            max={220}
            id="premium-music-min-bpm"
            value={minBpm}
            onChange={(event) => setMinBpm(event.target.value)}
          />
        </label>
        <label htmlFor="premium-music-max-bpm" className="space-y-2">
          <span className="text-sm font-medium">Maximum BPM</span>
          <Input
            type="number"
            min={40}
            max={220}
            id="premium-music-max-bpm"
            value={maxBpm}
            onChange={(event) => setMaxBpm(event.target.value)}
          />
        </label>
        <Button type="submit" className="w-fit">
          Search music
        </Button>
      </form>
      {formError ? <p role="alert">{formError}</p> : null}
      <p className="text-sm text-muted-foreground">
        Claire searches this same catalogue. Tempo filters exclude uploads whose
        BPM has not been measured. Audition tracks here; Claire chooses the
        soundtrack when creating an ad.
      </p>
      {result.isPending ? (
        <p role="status">Loading music…</p>
      ) : result.isError ? (
        <div role="alert" className="space-y-3">
          <p>Premium music is disabled or unavailable for this organization.</p>
          <Button variant="outline" onClick={() => void result.refetch()}>
            Retry
          </Button>
        </div>
      ) : (
        <>
          <p role="status" className="text-sm text-muted-foreground">
            Showing {tracks.length} of {result.data?.total ?? 0} matching tracks
          </p>
          {tracks.length === 0 ? (
            <p>No matching tracks. Try a name search without tempo filters.</p>
          ) : (
            <ul
              className="divide-y rounded-xl border"
              aria-label="Music search results"
            >
              {tracks.map((track) => {
                const key = `${track.source}-${track.id}`;
                const playing = key === playingId;
                return (
                  <li
                    key={key}
                    className="flex flex-wrap items-center gap-4 p-4"
                  >
                    <Button
                      variant={playing ? 'default' : 'outline'}
                      aria-label={`${playing ? 'Pause' : 'Play'} ${track.name}`}
                      onClick={() => toggle(key, track.playUrl)}
                    >
                      {playing ? 'Pause' : 'Play'}
                    </Button>
                    <div className="min-w-0 flex-1">
                      <p className="font-semibold">{track.name}</p>
                      <p className="text-sm text-muted-foreground">
                        {track.artist ??
                          (track.source === 'upload'
                            ? 'Organization upload'
                            : 'Shared library')}
                      </p>
                    </div>
                    <p className="text-sm tabular-nums">
                      {track.bpm === null ? 'BPM unknown' : `${track.bpm} BPM`}{' '}
                      ·{' '}
                      {track.durationSec === null
                        ? 'Duration unknown'
                        : `${Math.floor(Math.round(track.durationSec) / 60)}:${String(Math.round(track.durationSec) % 60).padStart(2, '0')}`}
                    </p>
                  </li>
                );
              })}
            </ul>
          )}
        </>
      )}
    </div>
  );
}
